import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { fmtINR } from "@/utils/format";

// Rendered in a portal so it is never clipped by overflow containers; `active` = keyboard-highlighted index
export function CustomerDropdown({ anchorRef, query, results, onPick, active = 0 }) {
  const [pos, setPos] = useState(null);
  useEffect(() => {
    const el = anchorRef?.current;
    if (!el || !query) { setPos(null); return; }
    const place = () => { const r = el.getBoundingClientRect(); setPos({ top: r.bottom + 4, left: r.left, width: Math.max(r.width, 320) }); };
    place();
    window.addEventListener("scroll", place, true); window.addEventListener("resize", place);
    return () => { window.removeEventListener("scroll", place, true); window.removeEventListener("resize", place); };
  }, [anchorRef, query]);
  useEffect(() => { document.querySelector(`[data-dd-active="true"]`)?.scrollIntoView({ block: "nearest" }); }, [active]);
  if (!query || !pos) return null;
  return createPortal(
    <div style={{ position: "fixed", top: pos.top, left: pos.left, width: pos.width, zIndex: 1000 }} className="bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden max-h-72 overflow-y-auto" data-testid="customer-dropdown">
      {results.length === 0 && <div className="px-3 py-2 text-sm text-slate-400">No customer matches “{query}”</div>}
      {results.map((m, i) => (
        <button key={m.id} type="button" data-dd-active={i === active ? "true" : undefined} onMouseDown={(e) => { e.preventDefault(); onPick(m); }} className={`w-full text-left px-3 py-2 hover:bg-slate-50 text-sm flex items-center gap-2 ${i === active ? "bg-blue-50 ring-1 ring-inset ring-[color:var(--bpcl-blue)]/40" : ""}`} data-testid={`cust-opt-${m.customer_code}`}>
          <div className="min-w-0 flex-1"><span className="font-medium">{m.name}</span> <span className="text-slate-400 text-xs">{m.firm_name ? `${m.firm_name} · ` : ""}{m.customer_code}{m.vehicle_numbers?.length ? ` · ${m.vehicle_numbers.join(", ")}` : ""}</span></div>
          {m.outstanding != null && <span className="font-mono-num text-xs text-slate-600">{fmtINR(m.outstanding)}</span>}
        </button>
      ))}
      <div className="px-3 py-1 text-[10px] text-slate-400 border-t border-slate-100 bg-slate-50">↑ ↓ move · Enter select · Esc close</div>
    </div>, document.body);
}

// Shared keyboard handler for a customer search input. Returns true if the key was consumed.
export function customerKeyNav(e, { results, active, setActive, pick, close }) {
  if (e.key === "ArrowDown") { e.preventDefault(); setActive(Math.min(active + 1, Math.max(results.length - 1, 0))); return true; }
  if (e.key === "ArrowUp") { e.preventDefault(); setActive(Math.max(active - 1, 0)); return true; }
  if (e.key === "Escape") { e.preventDefault(); close(); return true; }
  if (e.key === "Enter") { e.preventDefault(); const m = results[active] || results[0]; if (m) pick(m); return true; }
  return false;
}
